import React, { SyntheticEvent, useState } from "react";
import Guarantee from "../../../types/entities/Guarantee";
import { Button, Divider, Form, FormTextArea, Header, Icon, Image, ImageGroup, Modal, ModalActions, ModalContent, Popup } from "semantic-ui-react";
import GuaranteeFile from "../../../types/entities/GuaranteeFile";
import { formatDateTime } from "../../../utils/datetimeUtils";
import { formatMoney } from "../../../utils/numberUtils";
import { formatAddress } from "../../../utils/helpers";
import GuaranteeComment from "../../../types/entities/GuaranteeComment";
import { toast } from "react-toastify";
import { createGuaranteeComment } from "../../../services/guarantees.service";

interface Props {
    guarantee: Guarantee
};

const GuaranteeDetailModal = (props: Props) => {

    const [openModal, setOpenModal] = useState(false);
    const [comment, setComment] = useState("");

    const [guarantee, setGuarantee] = useState<Guarantee>(props.guarantee);

    const sortedComments = guarantee?.comments
        ? [...guarantee.comments].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        : [];

    const handleOpenModal = (event: SyntheticEvent) => {
        event.preventDefault();
        setOpenModal(true);
    };

    const handleCancelModal = (event: SyntheticEvent) => {
        event.preventDefault();
        setOpenModal(false);
    };

    const handleSubmit = async (event: SyntheticEvent) => {
        event.preventDefault();
        try {
            const response = await createGuaranteeComment(guarantee?.id, comment);
            if (response?.status === 201) {
                toast.success("Comentario agregado exitosamente.");
                setComment("");
                setGuarantee(response.data);
            } else {
                throw new Error("Error al agregar el comentario.");
            }
        } catch (error: any) {
            toast.error(error.message);
        }
    };

    return (
        <Modal
            closeOnDimmerClick
            closeIcon={true}
            size="large"
            onClose={(event) => handleCancelModal(event)}
            open={openModal}
            trigger={
                <Popup
                    trigger={
                        <Button secondary icon size="mini" onClick={(event) => handleOpenModal(event)}>
                            <Icon name="search" />
                        </Button>
                    }
                    content='Detalle de la garantia'
                    position='bottom center'
                    size='small'
                    inverted
                />
            } >
            <Header icon="list" content="Detalle de la garantía" />
            <ModalContent>

                <p><b>Fecha de creación:</b> {`${formatDateTime(guarantee?.createdAt)}`}</p>
                <p><b>Nombre del cliente:</b> {guarantee?.client?.person?.fullName}</p>
                <p><b>Dirección del cliente:</b> {formatAddress(guarantee?.address)}</p>
                <p><b>Saldo pendiente:</b> ${formatMoney(guarantee?.moneyRemaining)}</p>
                <p><b>Causa de la garantía:</b> {guarantee?.cause}</p>

                {guarantee?.solution && (
                    <p><b>Solución:</b> {guarantee?.solution}</p>
                )}

                {guarantee?.proofPayment && (
                    <Button icon href={guarantee?.proofPayment} target='_blank'>
                        <Icon name="attach" /> Comprobante de pago
                    </Button>
                )}

                {guarantee?.order && (
                    <Button icon href={`orders/${guarantee?.order?.id}/details`} target='_blank'>
                        <Icon name="cart" /> Orden de compra
                    </Button>
                )}

                <Divider />

                {guarantee?.guaranteeFiles === null ? (
                    <p style={{ color: "red" }}>*No se ha cargado ninguna evidencia de la garantía.</p>
                ) : (
                    <>
                        <p><b>Evidencia cargada</b><br />
                            *Para ver las evidencias, haga click en la imagen</p>
                        <ImageGroup size='tiny'>
                            {guarantee?.guaranteeFiles?.map((file: GuaranteeFile) => (
                                <Image
                                    key={file.id}
                                    src={file.fileName}
                                    href={file.fileName}
                                    target='_blank'
                                />
                            ))}
                        </ImageGroup>
                    </>
                )}

                <Divider />

                <Header as="h4">Comentarios</Header>

                <Form onSubmit={handleSubmit}>
                    <FormTextArea
                        placeholder="Agregar comentario..."
                        value={comment}
                        onChange={(event) => setComment(event.target.value)}
                    />
                    <Button color="yellow" icon type="submit" disabled={!comment}>
                        <Icon name="send" /> Agregar comentario
                    </Button>
                </Form>

                {sortedComments.length !== 0 && (
                    <>
                        {sortedComments.map((comment: GuaranteeComment) => (
                            <div key={comment.id} style={{ marginTop: "1rem", marginBottom: "1rem" }}>
                                <p style={{ marginBottom: "0.2rem" }}><b>{guarantee?.advisor?.person?.fullName}</b></p>
                                <p style={{ marginBottom: "0.2rem" }}><i>{formatDateTime(comment.createdAt)}</i></p>
                                <p>{comment.comment}</p>
                                <Divider />
                            </div>
                        ))}
                    </>
                )}

            </ModalContent>
            <ModalActions>
                <Button onClick={(event) => handleCancelModal(event)}>
                    <Icon name="cancel" /> Cerrar
                </Button>
            </ModalActions>
        </Modal>
    );
};

export default GuaranteeDetailModal;